import { Injectable, inject } from '@angular/core';
import { LaboratoryResourceService } from '@skyeng/libs/laboratory/resource';
import { type Observable, Subject, type Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { type ErrorReportUploadProvider } from './interfaces';

/**
 * Shared upload provider for all bug-report surfaces.
 *
 * Uses the existing resource upload-intention flow: calls
 * `POST /api/v1/file/upload-intension` to get S3 form fields, then uploads
 * the file directly to S3 through `SkyResourceService.uploadFileToS3` —
 * the same pipeline used by AI Tutor attachments (SINGULAR-202).
 *
 * Wire via ERROR_REPORT_UPLOAD_PROVIDER token in each surface's root providers.
 * The consuming app must also provide `LaboratoryResouceConfig` (via
 * `provideLaboratoryResouceConfig`) with the correct domain and upload path.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportUploadService implements ErrorReportUploadProvider {
  private laboratoryResourceService = inject(LaboratoryResourceService);

  private uploading$ = new Subject<unknown>();

  uploadFile(file: File | Blob, _uploadInitiator?: string): Observable<string> {
    const name = file instanceof File ? file.name : `screenshot-${Date.now()}.png`;

    return this.laboratoryResourceService
      .uploadIntention({ name, type: file.type, size: file.size })
      .pipe(
        switchMap(data =>
          this.laboratoryResourceService
            .uploadFile(data.url, file, data.inputs)
            .pipe(map(() => `${data.url}/${data.inputs.key}`)),
        ),
      );
  }

  onUploading(cb: (event: unknown) => unknown): Subscription {
    return this.uploading$.subscribe(cb);
  }
}
